import React, { useEffect, useState, useMemo, forwardRef } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  Divider,
  LinearProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PostAddIcon from "@mui/icons-material/PostAdd";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";

// Premium Animations
const slideUp = keyframes`
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(33, 150, 243, 0); }
  100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
  50% { box-shadow: 0 0 25px rgba(33, 150, 243, 0.6); }
  100% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function SubscriptionPlanManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletePlanName, setDeletePlanName] = useState("");
  const [formMode, setFormMode] = useState("add");
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter states
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    duration_days: "",
    post_limit: "",
    view_limit: "",
    price: "",
    features: [],
    is_free: false,
    sort_order: "",
    status: "active",
  });
  const [featureInput, setFeatureInput] = useState("");

  const token = localStorage.getItem("token");

  // Fetch all subscription plans
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/subscription-plan/get-subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPlans(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch plans", "error");
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      showSnackbar(error.response?.data?.message || "Error fetching plans", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle free plan toggle
  const handleFreePlanToggle = (checked) => {
    setFormData((prev) => ({
      ...prev,
      is_free: checked,
      price: checked ? 0 : prev.price,
    }));
  };

  // Add feature to list
  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput("");
    }
  };

  // Remove feature from list
  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // Handle form dialog open
  const handleOpenDialog = (mode = "add", item = null) => {
    setFormMode(mode);
    if (mode === "edit" && item) {
      setSelectedId(item._id);
      setFormData({
        name: item.name || "",
        duration_days: item.duration_days || "",
        post_limit: item.post_limit || "",
        view_limit: item.view_limit || "",
        price: item.price || "",
        features: item.features || [],
        is_free: item.is_free || false,
        sort_order: item.sort_order || "",
        status: item.status || "active",
      });
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  // Handle view dialog open
  const handleViewDialog = (plan) => {
    setSelectedPlan(plan);
    setViewDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      duration_days: "",
      post_limit: "",
      view_limit: "",
      price: "",
      features: [],
      is_free: false,
      sort_order: "",
      status: "active",
    });
    setFeatureInput("");
    setSelectedId(null);
  };

  // Handle form submit
  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      showSnackbar("Plan name is required", "warning");
      return;
    }
    if (!formData.duration_days) {
      showSnackbar("Duration is required", "warning");
      return;
    }
    if (!formData.post_limit && formData.post_limit !== 0) {
      showSnackbar("Post limit is required", "warning");
      return;
    }
    if (!formData.sort_order) {
      showSnackbar("Sort order is required", "warning");
      return;
    }
    if (!formData.is_free && !formData.price) {
      showSnackbar("Price is required for paid plans", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      const payload = {
        name: formData.name,
        duration_days: parseInt(formData.duration_days),
        post_limit: parseInt(formData.post_limit),
        view_limit: parseInt(formData.view_limit) || 0,
        price: formData.is_free ? 0 : parseFloat(formData.price),
        features: formData.features,
        is_free: formData.is_free,
        sort_order: parseInt(formData.sort_order),
        status: formData.status,
      };

      if (formMode === "add") {
        response = await axios.post(
          `${BASE_URL}/subscription-plan/create-subscription-plan`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        response = await axios.put(
          `${BASE_URL}/subscription-plan/update-subscription-plan?id=${selectedId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.data.success) {
        showSnackbar(`Plan ${formMode === "add" ? "added" : "updated"} successfully!`, "success");
        fetchPlans();
        setOpenForm(false);
        resetForm();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showSnackbar(error.response?.data?.message || "Operation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete dialog open
  const handleOpenDeleteDialog = (id, name) => {
    setDeleteId(id);
    setDeletePlanName(name);
    setDeleteDialogOpen(true);
  };

  // Handle delete plan
  const handleDeletePlan = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.delete(
        `${BASE_URL}/subscription-plan/delete-subscription-plan?id=${deleteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        showSnackbar("Plan deleted successfully!", "success");
        fetchPlans();
        setDeleteDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Delete failed", "error");
      }
    } catch (error) {
      console.error("Error deleting plan:", error);
      showSnackbar(error.response?.data?.message || "Failed to delete plan", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Clear filters
  const clearFilters = () => {
    setFilterName("");
    setFilterStatus("");
    setFilterType("");
  };

  // Filtered plans
  const filteredPlans = useMemo(() => {
    let filtered = [...plans];

    if (filterName) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((item) => item.status === filterStatus);
    }

    if (filterType) {
      filtered = filtered.filter((item) => (filterType === "free" ? item.is_free : !item.is_free));
    }

    return filtered;
  }, [plans, filterName, filterStatus, filterType]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format price
  const formatPrice = (price, isFree) => {
    if (isFree) return "FREE";
    return `₹${price}`;
  };

  // Get status chip
  const getStatusChip = (status) => {
    return status === "active" ? (
      <Chip
        label="ACTIVE"
        size="small"
        sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: "bold" }}
      />
    ) : (
      <Chip
        label="INACTIVE"
        size="small"
        sx={{ bgcolor: "#ffebee", color: "#d32f2f", fontWeight: "bold" }}
      />
    );
  };

  const columns = [
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       S.No
    //     </MDTypography>
    //   ),
    //   accessor: "sno",
    //   align: "center",
    //   width: "5%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PLAN NAME
        </MDTypography>
      ),
      accessor: "name",
      align: "left",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          PRICE
        </MDTypography>
      ),
      accessor: "price",
      align: "center",
      width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          DURATION
        </MDTypography>
      ),
      accessor: "duration",
      align: "center",
      width: "10%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          POSTS
        </MDTypography>
      ),
      accessor: "post_limit",
      align: "center",
      width: "8%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          VIEWS
        </MDTypography>
      ),
      accessor: "view_limit",
      align: "center",
      width: "8%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          TYPE
        </MDTypography>
      ),
      accessor: "type",
      align: "center",
      width: "8%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
      width: "8%",
    },
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       CREATED
    //     </MDTypography>
    //   ),
    //   accessor: "createdAt",
    //   align: "center",
    //   width: "10%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
      width: "18%",
    },
  ];

  const rows = useMemo(
    () =>
      filteredPlans.map((plan, index) => ({
        sno: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {index + 1}
          </MDTypography>
        ),
        name: (
          <MDBox>
            <MDTypography variant="button" fontWeight="bold" color="dark">
              {plan.name}
            </MDTypography>
            {/* {plan.is_free && (
              <Chip
                label="FREE"
                size="small"
                sx={{ ml: 1, bgcolor: "#ff9800", color: "white", fontSize: "0.7rem" }}
              />
            )} */}
          </MDBox>
        ),
        price: (
          <MDTypography variant="h6" fontWeight="bold" color={plan.is_free ? "success" : "error"}>
            {formatPrice(plan.price, plan.is_free)}
          </MDTypography>
        ),
        duration: (
          <MDTypography variant="caption" fontWeight="medium">
            {plan.duration_days} Days
          </MDTypography>
        ),
        post_limit: (
          <MDTypography variant="caption" fontWeight="medium">
            {plan.post_limit === 0 ? "Unlimited" : plan.post_limit}
          </MDTypography>
        ),
        view_limit: (
          <MDTypography variant="caption" fontWeight="medium">
            {plan.view_limit === 0 ? "Unlimited" : plan.view_limit}
          </MDTypography>
        ),
        type: (
          <Chip
            label={plan.is_free ? "FREE" : "PAID"}
            size="small"
            sx={{
              bgcolor: plan.is_free ? "#fff3e0" : "#e8f5e9",
              color: plan.is_free ? "#ed6c02" : "#2e7d32",
              fontWeight: "bold",
            }}
          />
        ),
        status: <MDBox textAlign="center">{getStatusChip(plan.status)}</MDBox>,
        createdAt: (
          <MDTypography variant="caption" fontWeight="medium" textAlign="center">
            {formatDate(plan.createdAt)}
          </MDTypography>
        ),
        action: (
          <MDBox display="flex" justifyContent="center" gap={0}>
            <Tooltip title="View Details">
              <IconButton
                color="default"
                onClick={() => handleViewDialog(plan)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Plan">
              <IconButton
                color="info"
                onClick={() => handleOpenDialog("edit", plan)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Plan">
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteDialog(plan._id, plan.name)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      })),
    [filteredPlans]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <WorkspacePremiumIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Subscription Plan Management
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  {/* <MDButton
                    variant="gradient"
                    color="dark"
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ borderRadius: "12px", textTransform: "none" }}
                  >
                    Filters
                  </MDButton> */}
                  <MDButton
                    variant="gradient"
                    color="dark"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog("add")}
                    sx={{ borderRadius: "12px", textTransform: "none" }}
                  >
                    Add Plan
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Filters Section */}
              {!showFilters && (
                <Fade in={!showFilters}>
                  <Box sx={{ pt: 3, pl: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Search by Plan Name"
                          value={filterName}
                          onChange={(e) => setFilterName(e.target.value)}
                          placeholder="Enter plan name..."
                          variant="outlined"
                          size="small"
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          variant="outlined"
                          size="small"
                          SelectProps={{ native: true }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        >
                          <option value="">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value)}
                          variant="outlined"
                          size="small"
                          SelectProps={{ native: true }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        >
                          <option value="">All Types</option>
                          <option value="free">Free Plans</option>
                          <option value="paid">Paid Plans</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                          fullWidth
                          sx={{ borderRadius: "10px", height: "40px" }}
                        >
                          Clear
                        </MDButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Fade>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredPlans.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No subscription plans found. Add your first plan.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* PREMIUM ADD/EDIT DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => !isSubmitting && setOpenForm(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #2196f3, #64b5f6, #2196f3)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setOpenForm(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(33,150,243,0.08) 0%, rgba(33,150,243,0) 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <DialogTitle sx={{ pb: 1, pt: 3, position: "relative", zIndex: 1 }}>
          <Grow in={openForm} timeout={600}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <Zoom in={openForm} timeout={600}>
                <MDBox
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                >
                  {formMode === "add" ? (
                    <AddIcon sx={{ fontSize: 32, color: "white" }} />
                  ) : (
                    <UpdateIcon sx={{ fontSize: 32, color: "white" }} />
                  )}
                </MDBox>
              </Zoom>

              <MDBox>
                <MDTypography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {formMode === "add" ? "Add Subscription Plan" : "Update Subscription Plan"}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  {formMode === "add"
                    ? "Create a new subscription plan"
                    : "Edit existing plan details"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={openForm} timeout={800}>
            <MDBox pt={3} pb={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "100ms" }}>
                    <TextField
                      fullWidth
                      label="Plan Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter plan name"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "150ms" }}>
                    <TextField
                      fullWidth
                      label="Sort Order"
                      name="sort_order"
                      type="number"
                      value={formData.sort_order}
                      onChange={handleInputChange}
                      placeholder="Enter sort order"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "200ms" }}>
                    <TextField
                      fullWidth
                      label="Duration (Days)"
                      name="duration_days"
                      type="number"
                      value={formData.duration_days}
                      onChange={handleInputChange}
                      placeholder="Enter duration in days"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "250ms" }}>
                    <TextField
                      fullWidth
                      label="Post Limit (0 for Unlimited)"
                      name="post_limit"
                      type="number"
                      value={formData.post_limit}
                      onChange={handleInputChange}
                      placeholder="Enter post limit"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "300ms" }}>
                    <TextField
                      fullWidth
                      label="View Limit (0 for Unlimited)"
                      name="view_limit"
                      type="number"
                      value={formData.view_limit}
                      onChange={handleInputChange}
                      placeholder="Enter view limit"
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "350ms" }}>
                    <TextField
                      fullWidth
                      label="Price (₹)"
                      name="price"
                      type="number"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="Enter price"
                      variant="outlined"
                      disabled={formData.is_free}
                      required={!formData.is_free}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "400ms" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.is_free}
                          onChange={(e) => handleFreePlanToggle(e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#ff9800" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#ff9800",
                            },
                          }}
                        />
                      }
                      label={
                        <MDTypography variant="button" fontWeight="medium">
                          Free Plan
                        </MDTypography>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "450ms" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status === "active"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.checked ? "active" : "inactive",
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#4caf50",
                            },
                          }}
                        />
                      }
                      label={
                        <MDTypography variant="button" fontWeight="medium">
                          {formData.status === "active" ? "Active" : "Inactive"}
                        </MDTypography>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Zoom>
                </Grid>

                {/* Features Section */}
                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "500ms" }}>
                    <Box>
                      <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                        Plan Features
                      </MDTypography>
                      <Box display="flex" gap={1} mb={2}>
                        <TextField
                          fullWidth
                          size="small"
                          value={featureInput}
                          onChange={(e) => setFeatureInput(e.target.value)}
                          placeholder="Enter feature (e.g., 24/7 Support)"
                          onKeyPress={(e) => e.key === "Enter" && handleAddFeature()}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                        />
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={handleAddFeature}
                          sx={{ borderRadius: "10px", textTransform: "none", minWidth: "100px" }}
                        >
                          Add
                        </MDButton>
                      </Box>

                      {formData.features.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          {formData.features.map((feature, idx) => (
                            <Paper
                              key={idx}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                bgcolor: "#f5f5f5",
                                borderRadius: "10px",
                                transition: "all 0.3s",
                                "&:hover": { bgcolor: "#eeeeee", transform: "translateX(5px)" },
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 18 }} />
                                <MDTypography variant="body2">{feature}</MDTypography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFeature(idx)}
                                sx={{ color: "#f44336" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Zoom>
                </Grid>
              </Grid>
            </MDBox>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            justifyContent: "flex-end",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "550ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => {
                setOpenForm(false);
                resetForm();
              }}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                borderColor: "#111",
                color: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>

          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "600ms" }}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
              }
            >
              {isSubmitting ? "PROCESSING..." : formMode === "add" ? "SAVE" : "UPDATE"}
            </MDButton>
          </Zoom>
        </DialogActions>
      </Dialog>

      {/* VIEW PLAN DIALOG */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <IconButton
          onClick={() => setViewDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle sx={{ pb: 1, pt: 3, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Zoom in={viewDialogOpen} timeout={600}>
            <MDBox display="flex" flexDirection="column" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                  animation: `${float} 2s ease-in-out infinite`,
                }}
              >
                <WorkspacePremiumIcon sx={{ fontSize: 45, color: "#fff" }} />
              </MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                {selectedPlan?.name}
              </MDTypography>
              {selectedPlan?.is_free && (
                <Chip
                  label="FREE PLAN"
                  sx={{ bgcolor: "#ff9800", color: "#fff", fontWeight: "bold" }}
                />
              )}
            </MDBox>
          </Zoom>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={viewDialogOpen} timeout={800}>
            <Box sx={{ py: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <AttachMoneyIcon sx={{ color: "#f44336" }} />
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        PRICE
                      </MDTypography>
                    </Box>
                    <MDTypography
                      variant="h4"
                      fontWeight="bold"
                      color={selectedPlan?.is_free ? "success" : "error"}
                    >
                      {formatPrice(selectedPlan?.price, selectedPlan?.is_free)}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CalendarTodayIcon sx={{ color: "#2196f3" }} />
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        DURATION
                      </MDTypography>
                    </Box>
                    <MDTypography variant="h4" fontWeight="bold">
                      {selectedPlan?.duration_days} Days
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <PostAddIcon sx={{ color: "#4caf50" }} />
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        POST LIMIT
                      </MDTypography>
                    </Box>
                    <MDTypography variant="h4" fontWeight="bold">
                      {selectedPlan?.post_limit === 0 ? "Unlimited" : selectedPlan?.post_limit}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <VisibilityOffIcon sx={{ color: "#ff9800" }} />
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        VIEW LIMIT
                      </MDTypography>
                    </Box>
                    <MDTypography variant="h4" fontWeight="bold">
                      {selectedPlan?.view_limit === 0 ? "Unlimited" : selectedPlan?.view_limit}
                    </MDTypography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <StarIcon sx={{ color: "#ffc107" }} />
                      <MDTypography variant="caption" color="text" fontWeight="bold">
                        FEATURES
                      </MDTypography>
                    </Box>
                    <Grid container spacing={1}>
                      {selectedPlan?.features?.map((feature, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 16 }} />
                            <MDTypography variant="body2">{feature}</MDTypography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Status
                        </MDTypography>
                        <MDBox mt={1}>{getStatusChip(selectedPlan?.status)}</MDBox>
                      </Grid>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Sort Order
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="bold">
                          {selectedPlan?.sort_order}
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: "16px", bgcolor: "#f8f9fa" }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Created At
                        </MDTypography>
                        <MDTypography variant="body2">
                          {formatDate(selectedPlan?.createdAt)}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" fontWeight="bold">
                          Last Updated
                        </MDTypography>
                        <MDTypography variant="body2">
                          {formatDate(selectedPlan?.updatedAt)}
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid> */}
              </Grid>
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "right" }}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setViewDialogOpen(false)}
            sx={{ borderRadius: "12px", px: 4 }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* PREMIUM DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(239, 68, 68, 0.1)",
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />

        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 40, color: "white" }} />
                  </Box>
                </Box>
              </Zoom>

              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete Plan?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>

              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Paper
                  elevation={0}
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      borderColor: "rgba(239, 68, 68, 0.4)",
                    },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    PLAN TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    &quot;{deletePlanName}&quot;
                  </MDTypography>
                </Paper>
              </Grow>

              <Fade in={deleteDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    This plan will be permanently removed from the system
                  </MDTypography>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                borderColor: "#111",
                color: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>

          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeletePlan}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
              }
            >
              {isSubmitting ? "DELETING..." : "DELETE PERMANENTLY"}
            </MDButton>
          </Zoom>
        </DialogActions>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default SubscriptionPlanManagement;
